import {
  iconProdutosVbPreenchido,
  iconNavVisualizar,
  iconNavRelogio,
  iconViagemNoExterior,
  iconDadosFamilia,
  iconDocAnalise,
  iconDocDocumento,
  iconFinCartao,
  iconFinGrafico,
  iconDoc2aVia,
  iconDadosCorretores,
  iconNavBusca,
  iconNavPasta,
  iconDocCalendario,
  iconNavFechar,
  iconNavAdicionar1,
  iconDocGenericoPreco
} from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsInput, BsdsIcon } from '@bsds-lib-component/react';
import React, { useId, useState } from 'react';

import { useWebVida } from '../../hooks';

// Icon and color configuration for each item
const ICON_CONFIGS = [
  { icon: iconDocAnalise, color: 'text-blue-500' },
  { icon: iconDadosCorretores, color: 'text-purple-500' },
  { icon: iconFinCartao, color: 'text-green-500' },
  { icon: iconFinGrafico, color: 'text-orange-500' },
  { icon: iconDocDocumento, color: 'text-red-500' },
  { icon: iconDoc2aVia, color: 'text-indigo-500' },
  { icon: iconNavBusca, color: 'text-teal-500' },
  { icon: iconNavPasta, color: 'text-pink-500' },
  { icon: iconDocCalendario, color: 'text-cyan-500' },
  { icon: iconNavFechar, color: 'text-rose-500' },
  { icon: iconNavAdicionar1, color: 'text-emerald-500' },
  { icon: iconDocGenericoPreco, color: 'text-violet-500' },
];

const WebVida: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputId = useId();
  const linksRegionId = useId();

  const { filteredLinks } = useWebVida({ searchTerm });

  // Function to get icon and color based on link ID (ensures consistency even after filters)
  const getIconConfig = (linkId: string) => {
    const idNumber = parseInt(linkId, 10) || 0;
    return ICON_CONFIGS[(idNumber - 1) % ICON_CONFIGS.length];
  };

  const handleCardClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCardKeyDown = (url: string, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick(url);
    }
  };

  return (
    <div
      className="bg-background-secondary bsds-flex-direction-column"
      role="main"
    >
      {/* Portal info */}
      <div className="bsds-display-flex bsds-align-items-center">
        <div className="w-[2.5px] h-8  bg-red-600 bsds-mr-12" aria-hidden="true" />
        <h2 className="opacity-80">WebVida</h2>
      </div>

      {/* Portal info cards */}
      <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center bsds-mt-24 bsds-ml-4">
        <BsdsIcon icon={iconProdutosVbPreenchido} className="w-4 h-4" aria-hidden="true" />
        <h6 className="bsds-ml-8 font-semibold">Acesso ao portal WebVida</h6>
      </div>

      {/* Info Cards */}
      <div
        className="mb-6 bsds-mt-24 flex justify-center"
        role="region"
        aria-label="Resumo de informações do portal"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 items-stretch w-full justify-items-center md:justify-items-center max-w-[1000px]">
          {/* Portal Status Card */}
          <BsdsCard className="p-4 w-full sm:w-full md:w-full lg:w-full xl:w-[220px]" platform="web" role="region" aria-label="Status do portal">
            <div className="flex justify-between items-center h-full">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-text-color mb-2 uppercase">Portal Status</span>
                <span className="text-md font-bold text-green-600" aria-label="Status do portal: Online">
                  Online
                </span>
              </div>
              <BsdsIcon icon={iconViagemNoExterior} className="w-5 h-5 text-green-600" aria-hidden="true" />
            </div>
          </BsdsCard>

          {/* Active Users Card */}
          <BsdsCard className="p-4 w-full sm:w-full md:w-full lg:w-full xl:w-[220px]" platform="web" role="region" aria-label="Quantidade de usuários ativos">
            <div className="flex justify-between items-center h-full">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-text-color mb-2 uppercase">Usuários Ativos</span>
                <span className="text-md font-bold text-secondary-600" aria-label="Usuários ativos: 1.234">
                  1.234
                </span>
              </div>
              <BsdsIcon icon={iconDadosFamilia} className="w-5 h-5 text-secondary-500" aria-hidden="true" />
            </div>
          </BsdsCard>

          {/* Today's Accesses Card */}
          <BsdsCard className="p-4 w-full sm:w-full md:w-full lg:w-full xl:w-[220px]" platform="web" role="region" aria-label="Acessos hoje">
            <div className="flex justify-between items-center h-full">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-text-color mb-2 uppercase">Acessos Hoje</span>
                <span className="text-md font-bold text-amber-700" aria-label="Acessos hoje: 456">
                  456
                </span>
              </div>
              <BsdsIcon icon={iconNavVisualizar} className="w-5 h-5 text-amber-700" aria-hidden="true" />
            </div>
          </BsdsCard>

          {/* Average Usage Time Card */}
          <BsdsCard className="p-4 w-full sm:w-full md:w-full lg:w-full xl:w-[220px]" platform="web" role="region" aria-label="Tempo médio de uso">
            <div className="flex justify-between items-center h-full">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-text-color mb-2 uppercase">Tempo de Uso</span>
                <span className="text-md font-bold text-blue-600" aria-label="Tempo médio de uso: 12min">
                  12min
                </span>
              </div>
              <BsdsIcon icon={iconNavRelogio} className="w-5 h-5 text-blue-600" aria-hidden="true" />
            </div>
          </BsdsCard>
        </div>
      </div>

      {/* Fixed header with search */}
      <div className="bsds-width-100 bg-background-secondary  bsds-border-bottom">

        {/* Search field */}
        <div className="bsds-mb-16">
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <div className="flex-1 relative">
              <label
                htmlFor={searchInputId}
                className="text-sm font-medium text-gray-700 bsds-display-block bsds-mb-8"
              >
                Buscar links
              </label>
              <BsdsInput
                id={searchInputId}
                type="text"
                placeholder="Pesquisar por título ou descrição..."
                value={searchTerm}
                onInput={(e) => {
                  const target = e.target as HTMLBsdsInputElement;
                  const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                  setSearchTerm(String(value));
                }}
                platform="web"
                aria-describedby={`${searchInputId}-helper`}
                aria-label="Campo de busca de links"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content area for links */}
      <div className="bsds-width-100 bsds-pb-20">
        {/* Links grid */}
        <div
          id={linksRegionId}
          role="region"
          aria-label={`Lista de links (${filteredLinks.length})`}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 bsds-mb-8 bsds-ph-16 bsds-pt-16 bsds-pb-100"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
        >
          {filteredLinks.map((link) => {
            const iconConfig = getIconConfig(link.id);
            return (
              <BsdsCard
                key={link.id}
                className="cursor-pointer transition-all p-4 duration-200 border border-gray-100 hover:border-red-100 hover:bg-red-50/30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 aspect-square flex flex-col justify-center"
                platform="web"
              >
                <div
                  data-testid="bsds-card-action"
                  onClick={() => handleCardClick(link.url)}
                  onKeyDown={(event) => handleCardKeyDown(link.url, event)}
                  tabIndex={0}
                  role="link"
                  aria-label={`Abrir ${link.title} em nova aba`}
                >
                  {/* Icon in the center */}
                  <div className="bsds-display-flex bsds-justify-content-center bsds-mb-12">
                    <BsdsIcon icon={iconConfig.icon} className={`w-10 h-10 ${iconConfig.color}`} />
                  </div>

                  {/* Description */}
                  <div className="bsds-text-center">
                    <p className="text-xs text-gray-700 font-medium">
                      {link.description}
                    </p>
                  </div>
                </div>
              </BsdsCard>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredLinks.length === 0 && (
          <div className="bsds-display-flex bsds-justify-content-center bsds-align-items-center bsds-ph-16" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <p className="text-lg text-gray-600 bsds-mb-8">
                Nenhum link encontrado
              </p>
              <p className="text-sm text-gray-500">
                Tente pesquisar com outros termos
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebVida;

